import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Button, Grid, IconButton, Link, Paper, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Close as CloseIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { getCurrentlyLoggedInOrImpersonatingUser } from '../../services/auth';
import MyEnviroImpact from '../rescuerDashboard/rescuerDashboardDesktop/myFrus/Components/MyEnviroImpact';
import { Colors } from '../../assets/theme/Colors';
import { fetchEnvironmentImpact } from '../../actions/environmentImpact';
import { hideUnnasignedUserBanner } from '../../actions/uiPersisted';
import HtmlTooltip from '../../components/Common/HtmlTooltip';
import { fetchInstagramDataIfNeeded } from '../../actions/instagram';
import UnassignedUserMap from './components/UnassignedUserMap';

const paperSx = {
  padding: 2,
  height: '100%',
};

const textSx = {
  lineHeight: 1,
};

const iconSx = {
  color: Colors.primaryColor,
};

const containerSx = {
  padding: 2,
};

const startSiteTextSx = {
  color: Colors.white,
};

const UnassignedUserDashboard = () => {
  const dispatch = useDispatch();
  const environmentImpact = useSelector((state) => state.entities.environmentImpact);
  const hideBanner = useSelector((state) => state.ui.persisted.hideUnnasignedUserBanner);
  const instagramEntities = useSelector((state) => state.entities.instagramItems);
  const insagramItems = Object.values(instagramEntities.byId)
    .filter((item) => item.media_type === 'IMAGE')
    .slice(0, 6);
  const [showInfoBanner, setShowInfoBanner] = useState(!hideBanner);
  const user = getCurrentlyLoggedInOrImpersonatingUser();
  const currentYear = moment().year();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    dispatch(
      fetchEnvironmentImpact(
        false,
        moment().startOf('year').format('YYYY-MM-DD'),
        moment().endOf('year').format('YYYY-MM-DD')
      )
    );
    dispatch(fetchInstagramDataIfNeeded());
  }, []);

  const onClose = () => {
    setShowInfoBanner(false);
    dispatch(hideUnnasignedUserBanner());
  };

  const StartSiteButton = styled(Button)({
    textTransform: 'none',
    padding: '12px 48px',
    borderRadius: 0,
  });

  const RedButton = styled(Button)({
    backgroundColor: Colors.appBar.main,
    textTransform: 'none',
    boxShadow: '4px 4px 0px 0px rgba(66, 68, 90, 1)',
    color: Colors.white,
    borderRadius: 0,
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: '#C7382E',
      boxShadow: '4px 4px 0px 0px rgba(66, 68, 90, 0.8)',
    },
  });

  return (
    <Box display="grid" rowGap={2} mb={2}>
      {showInfoBanner && (
        <Paper sx={{ padding: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            It looks like we don't currently have a site in the area you indicated. However, there are still ways you
            can contribute to fighting hunger in your community!
          </Typography>
          <IconButton sx={iconSx} size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={paperSx}>
            <Typography variant="h5">
              {user.firstname} {user.lastname}, we're so glad you're here!
            </Typography>
            <Box py={2}>
              <Typography variant="body1">
                Food Rescue US is committed to ending hunger and reducing food waste by directly transferring fresh,
                usable food that would have otherwise been thrown away from grocers, restaurants, and other food
                industry sources to food insecure families throughout the U.S.{' '}
                <Box
                  component={Link}
                  href="https://foodrescue.us/history/"
                  sx={{ color: Colors.secondaryColor }}
                  target="_blank"
                  underline="hover"
                >
                  Learn how we got started.
                </Box>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={paperSx}>
            <Typography variant="h6">{currentYear} by the numbers</Typography>
            <Box py={2}>
              <MyEnviroImpact
                isLoading={environmentImpact.inflight} // To do: fetch yearly data
                environmentImpact={environmentImpact.all}
                iconSize={32}
                valueFontVariant="subtitle2"
                textStyle={textSx}
                textVariant="caption"
                hideHours
                hideMiles
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper style={{ height: '100%' }}>
            <Grid container>
              <Grid item xs={12} md={7} lg={8}>
                <Box
                  sx={[
                    { backgroundColor: Colors.primaryColor, clipPath: 'polygon(0 0, 100% 0, 90% 100%, 0% 100%)' },
                    containerSx,
                  ]}
                >
                  <Typography variant="h5" sx={startSiteTextSx}>
                    Start a site in your community
                  </Typography>
                  <Box py={2} maxWidth="85%">
                    <Typography variant="body1" sx={startSiteTextSx}>
                      Food Rescue US Site Directors bring different skill sets and experiences to our work, but they all
                      share a passion for their community and our mission!
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" marginRight={isMobileView ? 6 : 10}>
                    <Link
                      href="https://foodrescue.us/rescue-food/become-a-site-director/"
                      target="_blank"
                      underline="hover"
                    >
                      <StartSiteButton color="inherit" variant="contained">
                        Learn more
                      </StartSiteButton>
                    </Link>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Box sx={containerSx}>
                  <Box display="flex" justifyContent="center">
                    <Typography variant="h5">Be the rescue!</Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection='column'
                    justifyContent="space-around"
                    alignItems="center"
                    py={1}
                    flexWrap="wrap"
                  >
                    <Link
                      href="https://secure.qgiv.com/for/foodrescueusmain3"
                      target="_blank"
                      underline="hover"
                    >
                      <RedButton style={{marginRight: 5, marginTop: 10}}>Donate</RedButton>
                    </Link>

                    <Link
                      href="https://foodrescue.us/about/the-hunger-food-waste-crisis/"
                      target="_blank"
                      underline="hover"
                    >
                      <RedButton style={{marginRight: 5, marginTop: 10}}>Learn About Hunger</RedButton>
                    </Link>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={paperSx}>
                  <Typography variant="h5">Nearest sites</Typography>
                  <UnassignedUserMap />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={paperSx}>
            <Box display="flex" justifyContent="center" pb={2}>
              <Typography variant="h6">Keep up with Food Rescue US!</Typography>
            </Box>
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={3} style={{display: 'flex', justifyContent: 'center'}}>
                  <HtmlTooltip title="Facebook" withBorder>
                    <Link
                      href="https://www.facebook.com/FoodRescueUS"
                      target="_blank"
                      underline="hover"
                    >
                      <FacebookIcon fontSize="large" sx={iconSx} />
                    </Link>
                  </HtmlTooltip>
                </Grid>
                <Grid item xs={3} style={{display: 'flex', justifyContent: 'center'}}>
                  <HtmlTooltip title="Instagram" withBorder>
                    <Link
                      href="https://www.instagram.com/foodrescueus/"
                      target="_blank"
                      underline="hover"
                    >
                      <InstagramIcon fontSize="large" sx={iconSx} />
                    </Link>
                  </HtmlTooltip>
                </Grid>
                <Grid item xs={3} style={{display: 'flex', justifyContent: 'center'}}>
                  <HtmlTooltip title="Twitter" withBorder>
                    <Link href="https://twitter.com/foodrescueUS" target="_blank" underline="hover">
                      <TwitterIcon fontSize="large" sx={iconSx} />
                    </Link>
                  </HtmlTooltip>
                </Grid>
                <Grid item xs={3} style={{display: 'flex', justifyContent: 'center'}}>
                  <HtmlTooltip title="LinkedIn" withBorder>
                    <Link
                      href="https://www.linkedin.com/company/food-rescue-us/"
                      target="_blank"
                      underline="hover"
                    >
                      <LinkedInIcon fontSize="large" sx={iconSx} />
                    </Link>
                  </HtmlTooltip>
                </Grid>

                {insagramItems.map((item) => (
                  <Grid item xs={4} key={item.id}>
                    <Link href={item.permalink} target="_blank" underline="hover">
                      <img
                        src={item.media_url}
                        style={{
                          objectFit: 'cover',
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          aspectRatio: '1/ 1',
                        }}
                        alt="instagram"
                      />
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UnassignedUserDashboard;
